import { AccountLifecyclePanel } from './Lifecycle';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useSession, useData, post } from '@yingbaba/api';
import {
  PageTitle,
  AvatarUploader,
  Field,
  Button,
  ErrorNotice,
  Notice,
  useToast,
} from '@yingbaba/ui';
import { UserRound, ShieldCheck } from '@yingbaba/icons';
import { QRCodeSVG } from 'qrcode.react';
import { fullDatetime, siteURL } from '@yingbaba/utils';

interface SecurityStatus {
  email_verified: boolean;
  email_verified_at: string;
  mfa_enabled: boolean;
  mfa_recovery_code_count: number;
  email_retry_after?: number;
}
interface SecuritySession {
  id: string;
  namespace: string;
  ip: string;
  created_at: string;
  expires_at: string;
  absolute_expires_at: string;
  current: boolean;
}
interface MFASetup {
  secret: string;
  uri: string;
}
interface MFAEnableResult {
  recovery_codes: string[];
}
interface SecurityDelivery {
  sent: boolean;
  debug_token?: string;
  retry_after?: number;
}

type AccountTab = 'profile' | 'security' | 'sessions' | 'account';
const accountTabs: { key: AccountTab; label: string }[] = [
  { key: 'profile', label: '个人资料' },
  { key: 'security', label: '登录与安全' },
  { key: 'sessions', label: '设备与会话' },
  { key: 'account', label: '账号管理' },
];
function accountTab(value: string | null): AccountTab {
  return accountTabs.some((item) => item.key === value) ? (value as AccountTab) : 'profile';
}

function recoveryCodesText(codes: string[]) {
  return [
    '影爸爸双因素认证恢复码',
    '',
    ...codes,
    '',
    '每个恢复码只能使用一次。',
    '请离线保存，不要只保存在安装认证器的同一台手机上。',
  ].join('\n');
}

function downloadRecoveryCodes(codes: string[]) {
  const href = URL.createObjectURL(
    new Blob([recoveryCodesText(codes)], { type: 'text/plain;charset=utf-8' }),
  );
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = `yingbaba-mfa-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
  anchor.click();
  URL.revokeObjectURL(href);
}

function printRecoveryCodes(codes: string[]) {
  const frame = document.createElement('iframe');
  frame.title = '恢复码打印';
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>影爸爸双因素认证恢复码</title>
<style>body{font-family:system-ui,-apple-system,sans-serif;padding:32px;line-height:1.6;color:#111}h1{font-size:22px}pre{font-size:18px;line-height:1.8;padding:20px;border:1px solid #ddd;border-radius:12px}.note{color:#555}</style>
</head><body><h1>影爸爸双因素认证恢复码</h1><pre>${codes.join('\n')}</pre>
<p class="note">每个恢复码只能使用一次。请离线保存，不要只保存在安装认证器的同一台手机上。</p></body></html>`;
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 1000);
  };
  document.body.append(frame);
}

export function AccountSettings() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('tab');
  const tab = accountTab(requestedTab);
  const session = useSession(),
    query = useQueryClient(),
    toast = useToast();
  const profileReadOnly =
    !!session.data?.account.lifecycle_status && session.data.account.lifecycle_status !== 'active';
  const security = useData<SecurityStatus>('/auth/security', tab === 'security');
  const sessions = useData<SecuritySession[]>('/auth/security/sessions', tab === 'sessions');
  const wechat = useData<{ available: boolean; bound: boolean; subscribed: boolean }>(
    '/auth/wechat/binding',
    tab === 'security',
  );
  useEffect(() => {
    if (!requestedTab || requestedTab === tab) return;
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
  }, [params, requestedTab, setParams, tab]);
  const [emailCooldown, setEmailCooldown] = useState(0),
    [emailChangeSent, setEmailChangeSent] = useState(false);
  useEffect(() => {
    setEmailCooldown(security.data?.email_retry_after || 0);
  }, [security.data]);
  useEffect(() => {
    if (emailCooldown <= 0) return;
    const timer = setTimeout(() => setEmailCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [emailCooldown]);
  const [busy, setBusy] = useState(''),
    [error, setError] = useState<unknown>(null),
    [mfaSetup, setMfaSetup] = useState<MFASetup | null>(null),
    [recoveryCodes, setRecoveryCodes] = useState<string[]>([]),
    [mfaManageAction, setMfaManageAction] = useState<'recovery' | 'disable' | null>(null),
    [debugToken, setDebugToken] = useState(''),
    [securityEditor, setSecurityEditor] = useState<'password' | 'email' | 'mfa' | 'wechat' | null>(
      null,
    );
  useEffect(() => {
    if (tab !== 'security') {
      setSecurityEditor(null);
      setMfaSetup(null);
      setMfaManageAction(null);
      setRecoveryCodes([]);
    }
  }, [tab]);
  async function save(e: React.FormEvent<HTMLFormElement>, password = false) {
    e.preventDefault();
    if (busy || (!password && profileReadOnly)) return;
    setError(null);
    const values = Object.fromEntries(new FormData(e.currentTarget));
    if (password && values.new_password !== values.confirm_password) {
      setError(new Error('两次填写的新密码不一致'));
      return;
    }
    setBusy(password ? 'password' : 'profile');
    try {
      await post(password ? '/account/password' : '/account/profile', values);
      if (password) {
        query.clear();
        location.assign('/login?changed=1');
        return;
      }
      await query.invalidateQueries();
      toast('个人资料已更新');
    } catch (e) {
      setError(e);
    } finally {
      setBusy('');
    }
  }
  async function securityAction<T>(name: string, path: string, payload: unknown = {}) {
    setBusy(name);
    setError(null);
    try {
      const value = await post<T>(path, payload);
      await query.invalidateQueries();
      return { ok: true as const, value };
    } catch (e) {
      setError(e);
      if (name === 'verify-email' || name === 'change-email')
        await query.invalidateQueries({ queryKey: ['/auth/security'] });
      return { ok: false as const, value: undefined as T | undefined };
    } finally {
      setBusy('');
    }
  }
  async function requestEmailVerification() {
    const value = await securityAction<SecurityDelivery>(
      'verify-email',
      '/auth/security/email/request',
    );
    if (!value.ok) return;
    setEmailChangeSent(false);
    setDebugToken(value.value?.debug_token || '');
    setEmailCooldown(value.value?.retry_after || 60);
    toast('验证邮件已发送');
  }
  async function requestEmailChange(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    const result = await securityAction<SecurityDelivery>(
      'change-email',
      '/auth/security/email/change',
      values,
    );
    if (!result.ok) return;
    setEmailChangeSent(true);
    setEmailCooldown(result.value?.retry_after || 60);
    setDebugToken(result.value?.debug_token || '');
    form.reset();
    toast('请打开新邮箱中的确认链接，验证后才会更换登录地址');
  }
  async function setupMFA(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const password = String(new FormData(form).get('password') || '');
    const value = await securityAction<MFASetup>('mfa-setup', '/auth/security/mfa/setup', {
      password,
    });
    if (value.ok) form.reset();
    if (value.ok && value.value) setMfaSetup(value.value);
  }
  async function enableMFA(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const values = Object.fromEntries(new FormData(e.currentTarget));
    const value = await securityAction<MFAEnableResult>('mfa-enable', '/auth/security/mfa/enable', {
      ...values,
    });
    if (!value.ok || !value.value) return;
    setRecoveryCodes(value.value.recovery_codes);
    setMfaSetup(null);
    toast('双因素认证已启用');
  }
  async function regenerateMFARecoveryCodes(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    const result = await securityAction<MFAEnableResult>(
      'mfa-recovery',
      '/auth/security/mfa/recovery-codes/regenerate',
      values,
    );
    if (!result.ok || !result.value) return;
    setRecoveryCodes(result.value.recovery_codes);
    setMfaManageAction(null);
    form.reset();
    toast('新的恢复码已生成，旧恢复码已全部失效');
  }
  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodesText(recoveryCodes));
      toast('恢复码已复制');
    } catch (e) {
      setError(e);
    }
  }
  async function disableMFA(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const values = Object.fromEntries(new FormData(form));
    if ((await securityAction('mfa-disable', '/auth/security/mfa/disable', values)).ok) {
      setRecoveryCodes([]);
      setMfaManageAction(null);
      setSecurityEditor(null);
      toast('双因素认证已关闭');
      form.reset();
    }
  }
  async function revokeSession(id: string, current: boolean) {
    if (!(await securityAction('session-' + id, `/auth/security/sessions/${id}/revoke`)).ok) return;
    if (current) {
      query.clear();
      location.assign('/login');
      return;
    }
    toast('会话已撤销');
  }
  return (
    <>
      <PageTitle title="账号设置" description="维护个人资料、登录安全、设备会话与账号状态。" />
      <ErrorNotice error={error} />
      <div className="tabs" role="tablist" aria-label="账号设置分类" style={{ marginTop: 20 }}>
        {accountTabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? 'active' : ''}
            onClick={() => setParams({ tab: item.key })}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="grid-2" style={{ alignItems: 'start' }}>
        {tab === 'profile' && (
          <section className="panel stack" style={{ gridColumn: '1 / -1', maxWidth: 760 }}>
            <AvatarUploader
              name={session.data?.account.name || ''}
              src={session.data?.account.avatar_url}
              path="/account/avatar"
              disabled={profileReadOnly}
            />
            <h2 className="row account-section-title">
              <UserRound size={20} />
              个人资料
            </h2>
            <form className="form-stack" onSubmit={(e) => save(e)}>
              <Field
                label="显示姓名"
                htmlFor="profile-name"
                hint="团队成员将在协作中看到这个名字。"
              >
                <input
                  id="profile-name"
                  className="input"
                  name="name"
                  disabled={profileReadOnly}
                  defaultValue={session.data?.account.name}
                  required
                  minLength={2}
                  maxLength={40}
                  autoComplete="name"
                />
              </Field>
              <Field
                label="登录邮箱"
                htmlFor="profile-email"
                hint="邮箱验证和更换请在「登录与安全」中处理。"
              >
                <input
                  id="profile-email"
                  className="input"
                  value={session.data?.account.email || ''}
                  readOnly
                  type="email"
                  autoComplete="email"
                />
              </Field>
              <Button
                type="submit"
                loading={busy === 'profile'}
                disabled={!!busy || profileReadOnly}
              >
                保存资料
              </Button>
            </form>
          </section>
        )}
        {tab === 'security' && (
          <>
            <section className="panel stack">
              <div className="row spread" style={{ alignItems: 'flex-start' }}>
                <div className="stack" style={{ gap: 4 }}>
                  <h2 className="row account-section-title">
                    <ShieldCheck size={20} />
                    登录密码
                  </h2>
                  <p className="small muted">用于邮箱密码登录。修改后所有设备都需要重新登录。</p>
                </div>
                {securityEditor !== 'password' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSecurityEditor('password')}
                  >
                    修改密码
                  </Button>
                )}
              </div>
              {securityEditor === 'password' && (
                <>
                  <Notice>修改成功后，所有设备上的登录会话都会失效，请使用新密码重新登录。</Notice>
                  <form className="form-stack" onSubmit={(e) => save(e, true)}>
                    <Field label="当前密码" htmlFor="current-password">
                      <input
                        id="current-password"
                        className="input"
                        name="current_password"
                        type="password"
                        autoComplete="current-password"
                        required
                        maxLength={72}
                      />
                    </Field>
                    <Field
                      label="新密码"
                      htmlFor="new-password"
                      hint="至少 10 位；建议混合字母、数字和符号。"
                    >
                      <input
                        id="new-password"
                        className="input"
                        name="new_password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={10}
                        maxLength={72}
                      />
                    </Field>
                    <Field label="再次填写新密码" htmlFor="confirm-password">
                      <input
                        id="confirm-password"
                        className="input"
                        name="confirm_password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={10}
                        maxLength={72}
                      />
                    </Field>
                    <div className="row">
                      <Button type="submit" loading={busy === 'password'} disabled={!!busy}>
                        更新密码并重新登录
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!!busy}
                        onClick={() => setSecurityEditor(null)}
                      >
                        取消
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </section>

            <section className="panel stack">
              <div className="row spread" style={{ alignItems: 'flex-start' }}>
                <div className="stack" style={{ gap: 4 }}>
                  <h2 className="row account-section-title">
                    <ShieldCheck size={20} />
                    登录邮箱
                  </h2>
                  <p className="small muted" style={{ wordBreak: 'break-all' }}>
                    {session.data?.account.email || '—'}
                  </p>
                  <p className="small muted">
                    {!security.data
                      ? '正在读取验证状态…'
                      : security.data.email_verified
                        ? '已验证，可用于登录与账号安全通知。'
                        : '尚未验证，部分新业务操作会受限。'}
                  </p>
                </div>
                {securityEditor !== 'email' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSecurityEditor('email')}
                  >
                    更换邮箱
                  </Button>
                )}
              </div>
              {security.data && !security.data.email_verified && (
                <>
                  <Notice>
                    请验证登录邮箱，再公开发布内容和表达合作意向；验证前可准备草稿及使用已有项目工具。
                  </Notice>
                  <Button
                    type="button"
                    variant="secondary"
                    loading={busy === 'verify-email'}
                    disabled={!!busy || emailCooldown > 0}
                    onClick={() => void requestEmailVerification()}
                  >
                    {emailCooldown > 0 ? `${emailCooldown} 秒后可重发` : '发送邮箱验证邮件'}
                  </Button>
                  {debugToken && !emailChangeSent && (
                    <Notice>
                      本地开发模式验证 token：
                      <code style={{ wordBreak: 'break-all' }}>{debugToken}</code>
                    </Notice>
                  )}
                </>
              )}
              {securityEditor === 'email' && (
                <div className="stack">
                  <Notice>
                    先验证当前密码，再确认新邮箱。确认完成前，当前邮箱仍可登录；完成后所有设备需重新登录。
                  </Notice>
                  {emailChangeSent && <Notice>确认邮件已发送到新邮箱，请打开邮件完成更换。</Notice>}
                  <form className="form-stack" onSubmit={requestEmailChange}>
                    <Field label="新登录邮箱" htmlFor="new-email">
                      <input
                        id="new-email"
                        className="input"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        maxLength={255}
                      />
                    </Field>
                    <Field label="验证当前密码" htmlFor="email-password">
                      <input
                        id="email-password"
                        className="input"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        maxLength={72}
                      />
                    </Field>
                    {security.data?.mfa_enabled && (
                      <Field
                        label="双因素验证码 / 恢复码"
                        htmlFor="email-mfa"
                        hint="填写认证器验证码或尚未使用的一次性恢复码。"
                      >
                        <input
                          id="email-mfa"
                          className="input"
                          name="mfa_code"
                          autoComplete="one-time-code"
                          required
                          maxLength={32}
                        />
                      </Field>
                    )}
                    <div className="row">
                      <Button
                        type="submit"
                        variant="secondary"
                        loading={busy === 'change-email'}
                        disabled={!!busy || emailCooldown > 0}
                      >
                        {emailCooldown > 0
                          ? `${emailCooldown} 秒后可发送`
                          : '验证身份并发送确认邮件'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!!busy}
                        onClick={() => setSecurityEditor(null)}
                      >
                        取消
                      </Button>
                    </div>
                  </form>
                  {emailChangeSent && debugToken && (
                    <Notice>
                      本地验证链接：
                      <a
                        href={siteURL(
                          'www',
                          `/login?email_change_token=${encodeURIComponent(debugToken)}`,
                        )}
                      >
                        确认新邮箱
                      </a>
                    </Notice>
                  )}
                </div>
              )}
            </section>

            <section className="panel stack">
              <div className="row spread" style={{ alignItems: 'flex-start' }}>
                <div className="stack" style={{ gap: 4 }}>
                  <h2 className="row account-section-title">
                    <ShieldCheck size={20} />
                    双因素认证
                  </h2>
                  <p className="small muted">
                    {!security.data
                      ? '正在读取双因素认证状态…'
                      : security.data.mfa_enabled
                        ? `已开启。登录和敏感账号操作会要求认证器验证码或恢复码。剩余恢复码：${security.data.mfa_recovery_code_count ?? 0} 个。`
                        : '未开启。建议绑定认证器，为账号增加一层保护。'}
                  </p>
                </div>
                {security.data && securityEditor !== 'mfa' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setMfaManageAction(null);
                      setSecurityEditor('mfa');
                    }}
                  >
                    {security.data?.mfa_enabled ? '管理' : '启用'}
                  </Button>
                )}
              </div>
              {securityEditor === 'mfa' && !security.data?.mfa_enabled && !mfaSetup && (
                <form className="form-stack" onSubmit={setupMFA}>
                  <Field
                    label="验证当前密码"
                    htmlFor="mfa-setup-password"
                    hint="绑定认证器前需要确认是你本人操作。"
                  >
                    <input
                      id="mfa-setup-password"
                      className="input"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      maxLength={72}
                    />
                  </Field>
                  <div className="row">
                    <Button type="submit" loading={busy === 'mfa-setup'} disabled={!!busy}>
                      开始绑定认证器
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={!!busy}
                      onClick={() => setSecurityEditor(null)}
                    >
                      取消
                    </Button>
                  </div>
                </form>
              )}
              {securityEditor === 'mfa' && mfaSetup && (
                <form className="form-stack" onSubmit={enableMFA}>
                  <Notice>
                    扫描二维码后，输入认证器验证码并再次验证当前密码。启用成功后，其他设备的登录会话将失效。
                  </Notice>
                  <div
                    style={{
                      background: '#fff',
                      padding: 14,
                      width: 'fit-content',
                      borderRadius: 12,
                    }}
                  >
                    <QRCodeSVG value={mfaSetup.uri} size={180} />
                  </div>
                  <p className="small muted" style={{ wordBreak: 'break-all' }}>
                    手动密钥：<code>{mfaSetup.secret}</code>
                  </p>
                  <Field label="认证器验证码" htmlFor="mfa-enable-code">
                    <input
                      id="mfa-enable-code"
                      className="input"
                      name="code"
                      required
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      minLength={6}
                      maxLength={32}
                    />
                  </Field>
                  <Field label="再次验证当前密码" htmlFor="mfa-enable-password">
                    <input
                      id="mfa-enable-password"
                      className="input"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      maxLength={72}
                    />
                  </Field>
                  <div className="row">
                    <Button type="submit" loading={busy === 'mfa-enable'}>
                      确认启用
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={!!busy}
                      onClick={() => {
                        setMfaSetup(null);
                        setSecurityEditor(null);
                      }}
                    >
                      取消
                    </Button>
                  </div>
                </form>
              )}
              {securityEditor === 'mfa' && recoveryCodes.length > 0 && (
                <>
                  <Notice>
                    <strong>恢复码只显示这一次，请立即离线保存。</strong>
                    <p className="small" style={{ marginTop: 8 }}>
                      每个恢复码只能使用一次。不要只保存在安装认证器的同一台手机上。
                    </p>
                    <div className="mono" style={{ marginTop: 10, display: 'grid', gap: 4 }}>
                      {recoveryCodes.map((code) => (
                        <code key={code}>{code}</code>
                      ))}
                    </div>
                  </Notice>
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void copyRecoveryCodes()}
                    >
                      复制恢复码
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => downloadRecoveryCodes(recoveryCodes)}
                    >
                      下载 TXT
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => printRecoveryCodes(recoveryCodes)}
                    >
                      打印
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setRecoveryCodes([]);
                        setSecurityEditor(null);
                      }}
                    >
                      我已安全保存
                    </Button>
                  </div>
                </>
              )}
              {securityEditor === 'mfa' &&
                security.data?.mfa_enabled &&
                recoveryCodes.length === 0 && (
                  <>
                    <Notice>
                      当前还有 <strong>{security.data.mfa_recovery_code_count ?? 0}</strong>{' '}
                      个可用恢复码。 重新生成后，现有恢复码会立即全部失效。
                    </Notice>
                    {!mfaManageAction && (
                      <div className="row" style={{ flexWrap: 'wrap' }}>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setMfaManageAction('recovery')}
                        >
                          重新生成恢复码
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setMfaManageAction('disable')}
                        >
                          关闭双因素认证
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setSecurityEditor(null)}
                        >
                          完成
                        </Button>
                      </div>
                    )}
                    {mfaManageAction === 'recovery' && (
                      <form className="form-stack" onSubmit={regenerateMFARecoveryCodes}>
                        <Notice>
                          为防止他人替换你的恢复方式，请再次输入当前密码，并提供认证器验证码或一个尚未使用的恢复码。
                        </Notice>
                        <Field label="当前密码" htmlFor="mfa-recovery-password">
                          <input
                            id="mfa-recovery-password"
                            className="input"
                            name="password"
                            type="password"
                            required
                            autoComplete="current-password"
                          />
                        </Field>
                        <Field label="验证码 / 恢复码" htmlFor="mfa-recovery-code">
                          <input
                            id="mfa-recovery-code"
                            className="input"
                            name="code"
                            required
                            autoComplete="one-time-code"
                          />
                        </Field>
                        <div className="row">
                          <Button type="submit" loading={busy === 'mfa-recovery'}>
                            确认重新生成
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!!busy}
                            onClick={() => setMfaManageAction(null)}
                          >
                            取消
                          </Button>
                        </div>
                      </form>
                    )}
                    {mfaManageAction === 'disable' && (
                      <form className="form-stack" onSubmit={disableMFA}>
                        <Notice>关闭双因素认证需要当前密码和认证器验证码或恢复码。</Notice>
                        <Field label="当前密码" htmlFor="mfa-disable-password">
                          <input
                            id="mfa-disable-password"
                            className="input"
                            name="password"
                            type="password"
                            required
                            autoComplete="current-password"
                          />
                        </Field>
                        <Field label="验证码 / 恢复码" htmlFor="mfa-disable-code">
                          <input
                            id="mfa-disable-code"
                            className="input"
                            name="code"
                            required
                            autoComplete="one-time-code"
                          />
                        </Field>
                        <div className="row">
                          <Button
                            type="submit"
                            variant="secondary"
                            loading={busy === 'mfa-disable'}
                          >
                            关闭双因素认证
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={!!busy}
                            onClick={() => setMfaManageAction(null)}
                          >
                            取消
                          </Button>
                        </div>
                      </form>
                    )}
                  </>
                )}
            </section>

            <section className="panel stack">
              <div className="row spread" style={{ alignItems: 'flex-start' }}>
                <div className="stack" style={{ gap: 4 }}>
                  <h2 className="account-section-title">微信登录</h2>
                  <p className="small muted">
                    {!wechat.data?.available
                      ? '当前环境未启用微信扫码登录。'
                      : wechat.data.bound
                        ? `已绑定${wechat.data.subscribed ? '，并已关注影爸爸服务号。' : '。'}可使用微信扫码快速登录。`
                        : '未绑定。可在微信扫码登录流程中验证当前账号并完成绑定。'}
                  </p>
                </div>
                {wechat.data?.bound && securityEditor !== 'wechat' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSecurityEditor('wechat')}
                  >
                    管理
                  </Button>
                )}
              </div>
              {wechat.data?.bound && securityEditor === 'wechat' && (
                <form
                  className="form-stack"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const result = await securityAction(
                      'wechat-unbind',
                      '/auth/wechat/unbind',
                      Object.fromEntries(new FormData(form)),
                    );
                    if (result.ok) {
                      form.reset();
                      setSecurityEditor(null);
                      toast('微信登录绑定已解除');
                    }
                  }}
                >
                  <Notice>
                    解除绑定后，其他网页设备会话将退出；邮箱登录和小程序绑定不受影响。取消公众号关注请在微信中操作。
                  </Notice>
                  <Field label="验证当前密码" htmlFor="wechat-unbind-password">
                    <input
                      id="wechat-unbind-password"
                      className="input"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      maxLength={72}
                      required
                    />
                  </Field>
                  {security.data?.mfa_enabled && (
                    <Field label="双因素验证码" htmlFor="wechat-unbind-mfa">
                      <input
                        id="wechat-unbind-mfa"
                        className="input"
                        name="mfa_code"
                        autoComplete="one-time-code"
                        maxLength={32}
                        required
                      />
                    </Field>
                  )}
                  <div className="row">
                    <Button
                      type="submit"
                      variant="danger"
                      loading={busy === 'wechat-unbind'}
                      disabled={!!busy}
                    >
                      确认解除绑定
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={!!busy}
                      onClick={() => setSecurityEditor(null)}
                    >
                      取消
                    </Button>
                  </div>
                </form>
              )}
            </section>
          </>
        )}
        {tab === 'sessions' && (
          <section className="panel stack" style={{ gridColumn: '1 / -1', maxWidth: 900 }}>
            <div className="row spread">
              <h2 className="account-section-title">登录会话</h2>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                loading={busy === 'sessions-others'}
                onClick={async () => {
                  if (
                    (
                      await securityAction(
                        'sessions-others',
                        '/auth/security/sessions/others/revoke',
                      )
                    ).ok
                  )
                    toast('其他设备会话已撤销');
                }}
              >
                退出其他设备
              </Button>
            </div>
            {(sessions.data || []).map((item) => (
              <div className="list-item" key={item.id}>
                <div className="grow">
                  <strong>
                    {item.namespace === 'miniapp'
                      ? '微信小程序'
                      : item.namespace === 'admin'
                        ? '运营后台'
                        : '网页端'}
                    {item.current ? ' · 当前' : ''}
                  </strong>
                  <p className="small muted">登录 IP：{item.ip || '未记录'}</p>
                  <p className="small muted">登录时间：{fullDatetime(item.created_at)}</p>
                  {item.namespace === 'miniapp' ? (
                    <p className="small muted">
                      固定到期：{fullDatetime(item.absolute_expires_at)}
                    </p>
                  ) : (
                    <>
                      <p className="small muted">空闲到期：{fullDatetime(item.expires_at)}</p>
                      <p className="small muted">
                        最晚到期：{fullDatetime(item.absolute_expires_at)}
                      </p>
                    </>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={busy === 'session-' + item.id}
                  onClick={() => void revokeSession(item.id, item.current)}
                >
                  退出
                </Button>
              </div>
            ))}
            {!sessions.data?.length && <p className="small muted">暂无有效登录会话。</p>}
          </section>
        )}
        {tab === 'account' && (
          <div style={{ gridColumn: '1 / -1', maxWidth: 900 }}>
            <AccountLifecyclePanel />
          </div>
        )}
      </div>
    </>
  );
}
